<template>
  <basic-layout-dynamic>
    <div class="container">
      <div class="files" :style="{ width: canvasStyles.width }">
        <div class="file">
          <div class="file-name">
            <font-awesome-icon
              class="icon icon-with-text"
              icon="file-alt"
            ></font-awesome-icon>
            <a href="#" @click.prevent="$refs.fileLeft.click()">
              {{ leftFileName }}
            </a>
          </div>
        </div>
        <div class="file">
          <div class="file-name">
            <font-awesome-icon
              class="icon icon-with-text"
              icon="file-alt"
            ></font-awesome-icon>
            <a href="#" @click.prevent="$refs.fileRight.click()">{{
              rightFileName
            }}</a>
          </div>
        </div>
        <input
          hidden
          ref="fileLeft"
          type="file"
          multiple
          @change="setFiles($event.target.files, 'left')"
        />
        <input
          hidden
          ref="fileRight"
          type="file"
          multiple
          @change="setFiles($event.target.files, 'right')"
        />
      </div>
      <div>
        <div class="canvas-wrapper" :style="canvasStyles">
          <canvas ref="baseLayer" :style="canvasStyles"></canvas>
          <canvas ref="intersectionLayer" :style="canvasStyles"></canvas>
          <canvas ref="layer" :style="canvasStyles"></canvas>
          <transition>
            <div
              v-if="noFile || dragging"
              class="drop-area"
              :style="canvasStyles"
              @drop="dropFile"
            >
              <font-awesome-icon
                icon="file-alt"
                class="dnd-area-icon"
                :class="{
                  animated: dragging,
                  infinite: dragging,
                  heartBeat: dragging,
                }"
              />
            </div>
          </transition>
        </div>
      </div>
      <div class="actions">
        <gl-button
          primary
          @click="download"
          :disabled="!canDownload"
          icon-pre="download"
          >Download</gl-button
        >
      </div>

      <!-- 進捗ダイアログ -->
      <md-dialog
        :md-active.sync="downloading"
        :md-click-outside-to-close="false"
        :md-close-on-esc="false"
      >
        <md-dialog-content>
          <md-steppers
            md-alternative
            md-linear
            md-vertical
            :md-active-step.sync="activeStep"
          >
            <md-step
              id="uploading"
              md-label="Upload"
              :md-editable="false"
              :md-done.sync="completedSteps.uploading"
            >
              <p v-if="!invalidFile">
                顧客ファイルをアップロードしています
                <md-progress-bar md-mode="indeterminate"></md-progress-bar>
              </p>
              <p v-else>
                <span class="warn"
                  >顧客一覧ファイルが読み込めませんでした。</span
                >
              </p>
            </md-step>
            <md-step
              id="calculating"
              md-label="Calculate"
              :md-editable="false"
              :md-done.sync="completedSteps.calculating"
            >
              <p v-if="!noCustomersInSelectedAreas">
                対象顧客を計算しています
                <md-progress-bar md-mode="indeterminate" />
              </p>
              <p v-else>
                <span class="warn">対象顧客がいませんでした！</span>
              </p>
            </md-step>
            <md-step
              id="creating"
              md-label="Create"
              :md-editable="false"
              :md-done.sync="completedSteps.creating"
            >
              <p>
                ファイルを作成しています
                <md-progress-bar md-mode="indeterminate"></md-progress-bar>
              </p>
            </md-step>
            <md-step
              id="download-ready"
              md-label="Done!"
              :md-editable="false"
              :md-done.sync="completedSteps.downloadReady"
            >
              <p>
                できた！
                <a href="#" @click.prevent="downloadFile"
                  >ここからダウンロード</a
                >
              </p>
            </md-step>
          </md-steppers>
        </md-dialog-content>
        <md-dialog-actions v-if="canCloseDialog">
          <gl-button @click="downloading = false">Close</gl-button>
        </md-dialog-actions>
      </md-dialog>
    </div>
  </basic-layout-dynamic>
</template>

<script>
import { Subject } from 'rxjs'
import { debounceTime } from 'rxjs/operators'
import moment from 'moment'

// 1) leftCircleのみの領域
const AREA_SELECT_LEFT = 1
// 2) rightCircleのみの領域
const AREA_SELECT_RIGHT = 2
// 3) 重なる領域
const AREA_SELECT_INTERSECTION = 3
// 4) A, B以外の領域か判定
const AREA_SELECT_OTHERS = 4

class Circle {
  constructor({ x, y, raduis, inactiveColor, activeColor, active }) {
    this.x = x
    this.y = y
    this.raduis = raduis
    this.inactiveColor = inactiveColor
    this.activeColor = activeColor
    this.active = active
    this.doneDrawing = false
  }

  drawOn(context, phase) {
    if (this.doneDrawing) {
      phase = 1
    } else if (phase === 1) {
      this.doneDrawing = true
    }

    context.beginPath()
    context.arc(
      this.x,
      this.y,
      this.raduis * ease(phase),
      0,
      Math.PI * 2,
      false,
    )
    if (this.active) {
      context.fillStyle = this.activeColor
    } else {
      context.fillStyle = this.inactiveColor
    }
    context.fill()
    context.closePath()
  }
}

/**
 * 指定座標が円に重なっているか調べる
 */
function spotWidthin(x, y, circle) {
  if (!circle) {
    return false
  }
  // 判定対象の点（x, y）と円の中心からの距離が円の半径より小さければ円に入っていると判定
  const d = Math.sqrt(Math.pow(circle.x - x, 2) + Math.pow(circle.y - y, 2))
  return d <= circle.raduis
}

function ease(x) {
  return x * x * x + x * 1.4 * Math.sin(x * Math.PI)
}

/**
 * ベン図でポン！
 *
 * 顧客コード一覧ファイルを2つ選択し、2ファイルの重複顧客やファイルで列挙されている
 * 顧客以外の顧客を取得したりする機能を提供します。
 */
export default {
  props: {
    /**
     * ベン図描画領域の横幅
     */
    width: {
      type: Number,
      default: 600,
    },
  },
  data() {
    return {
      /**
       * ダウンロード中フラグ
       */
      downloading: false,
      /**
       * 左側ファイル
       */
      leftFile: undefined,
      /**
       * 左側サークル
       */
      leftCircle: undefined,
      /**
       * 右側ファイル
       */
      rightFile: undefined,
      /**
       * 右側サークル
       */
      rightCircle: undefined,
      /**
       * 重なり部分の円に関するプロパティ
       */
      intersectionCircle: undefined,
      /**
       * キャンバスのコンテキスト（背景用）
       */
      baseCtx: undefined,
      /**
       * キャンバスのコンテキスト（重なる部分描画用）
       */
      intersectionCtx: undefined,
      /**
       * キャンバスのコンテキスト（各ファイルの円用）
       */
      ctx: undefined,
      /**
       * キャンバスのプロパティ
       */
      canvas: {
        /**
         * 横幅
         */
        width: this.width,
        /**
         * 高さ
         */
        height: this.width * 0.6,
      },
      /**
       * 選択されているエリア
       */
      selectedAreas: new Set(),
      /**
       * ファイルDnD
       */
      dragOverEvents: new Subject(),
      dragging: false,
      /**
       *
       */
      activeStep: 'uploading',
      completedSteps: {
        uploading: false,
        calculating: false,
        creating: false,
        downloadReady: false,
      },
      downloadLink: undefined,
      noCustomersInSelectedAreas: false,
      invalidFile: false,
    }
  },
  computed: {
    /**
     * キャンバスの大きさ
     */
    canvasStyles() {
      return {
        width: `${this.canvas.width}px`,
        height: `${this.canvas.height}px`,
      }
    },
    /**
     * ファイル選択なし
     */
    noFile() {
      return !this.leftFile && !this.rightFile
    },
    /**
     * 左側ファイル名
     */
    leftFileName() {
      return this.leftFile ? this.leftFile.name : 'ファイルを選択してください'
    },
    /**
     * 右側ファイル名
     */
    rightFileName() {
      return this.rightFile ? this.rightFile.name : 'ファイルを選択してください'
    },
    /**
     * エリア選択が行われているか
     */
    isAreaSelected() {
      return this.selectedAreas.size > 0
    },
    canDownload() {
      if (!this.leftFile || !this.rightFile) {
        return false
      }
      if (this.downloading) {
        return false
      }
      if (!this.isAreaSelected) {
        return false
      }

      return true
    },
    canCloseDialog() {
      return (
        this.activeStep === 'download-ready' ||
        this.invalidFile ||
        this.noCustomersInSelectedAreas
      )
    },
  },
  methods: {
    /**
     * ドラッグ終了
     */
    endDrag(e) {
      this.dragging = false
      e.preventDefault()
    },
    /**
     * ファイルドロップ
     */
    dropFile(e) {
      const files = e.dataTransfer.files
      this.setFiles(files)
      e.preventDefault()
    },
    /**
     *
     */
    getDownloadFilename() {
      const l = this.leftFile ? this.leftFileName.substring(0, 5) : ''
      const r = this.rightFile ? this.rightFileName.substring(0, 5) : ''
      return `${l}_${r}_${moment(new Date()).format('YYYYMMDDHHmmss')}.txt`
    },
    setFiles(files, position) {
      if (!files || files.length === 0) {
        return
      }

      if (files.length > 2) {
        alert('選択できるファイルは2つまでです。')
        return
      }

      // 2ファイルでも片方のファイルでも選択状態はクリアします
      this.selectedAreas = new Set()
      if (this.leftCircle) {
        this.leftCircle.active = false
      }
      if (this.rightCircle) {
        this.rightCircle.active = false
      }
      if (this.intersectionCircle) {
        this.intersectionCircle.active = false
      }

      if (files.length === 2) {
        // 洗い替え
        this.leftFile = files[0]
        this.rightFile = files[1]
        this.deleteCircle('left')
        this.deleteCircle('right')
        this.addCircle('left')
        this.addCircle('right')
      } else if (files.length === 1) {
        if (position === 'left') {
          this.leftFile = files[0]
          this.deleteCircle('left')
          this.addCircle('left')
        } else if (position === 'right') {
          this.rightFile = files[0]
          this.deleteCircle('right')
          this.addCircle('right')
        } else {
          // TODO 既に2ファイルが選択されている状態でドラッグアンドドロップ
          //      された場合、どちらのファイルを差し替えるかのルール決め
          this.rightFile = files[0]
          this.deleteCircle('right')
          this.addCircle('right')
        }
      }
    },
    /**
     * 円追加
     */
    addCircle(position) {
      const y = this.canvas.height / 2 // 高さは中央にするので共通
      const raduis = this.canvas.width / 5 // 大きさは共通 TODO 本当は人数が少ない方を小さくしたい

      if (position === 'left') {
        const x = raduis + raduis / 2 + raduis / 3

        this.leftCircle = new Circle({
          x,
          y,
          raduis,
          inactiveColor: '#E0E0E0',
          activeColor: '#26A69A',
          active: false,
        })

        // 左の円を作成した段階で重なる部分描画用の円を裏に作成しておく
        this.intersectionCircle = new Circle({
          x,
          y,
          raduis,
          inactiveColor: '#BDBDBD',
          activeColor: '#5C6BC0',
          active: false,
        })
      } else if (position === 'right') {
        this.rightCircle = new Circle({
          x: raduis * 3 + raduis / 2 - raduis / 3,
          y,
          raduis,
          inactiveColor: '#9E9E9E',
          activeColor: '#FFA726',
          active: false,
        })
      }

      const duration = 500
      requestAnimationFrame(startedAt => {
        const self = this
        tick(startedAt)
        function tick(timestamp) {
          let phase = (timestamp - startedAt) / duration
          if (phase > 1) {
            phase = 1
            self.render(phase)
          } else {
            self.render(phase)
            requestAnimationFrame(tick)
          }
        }
      })
    },
    /**
     * 円削除
     */
    deleteCircle(position) {
      if (position === 'left') {
        this.leftCircle = undefined
        this.intersectionCircle = undefined
      } else if (position === 'right') {
        this.rightCircle = undefined
      }

      this.render()
    },
    /**
     * 円描画のアニメーションレンダリング
     */
    render(phase) {
      if (phase === undefined) {
        phase = 1
      }

      // とりあえず描画前にはcanvasは全てクリアする
      for (const ctx of [this.baseCtx, this.intersectionCtx, this.ctx]) {
        ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
      }

      this.baseCtx.fillStyle = this.selectedAreas.has(AREA_SELECT_OTHERS)
        ? '#00b0ff'
        : '#FAFAFA'
      this.baseCtx.fillRect(0, 0, this.canvas.width, this.canvas.height)
      this.baseCtx.textAlign = 'right'
      this.baseCtx.textBaseline = 'bottom'
      this.baseCtx.font = devicePixelRatio * 20 + 'px Meiryo,monospace'

      if (this.intersectionCircle) {
        this.intersectionCircle.drawOn(this.intersectionCtx, phase)
      }
      if (this.leftCircle) {
        this.leftCircle.drawOn(this.ctx, phase)
      }
      if (this.rightCircle) {
        this.rightCircle.drawOn(this.ctx, phase)
      }
    },
    /**
     * キャンバス上でクリックされた座標から選択域を取得する
     *
     * * 左円のみの領域
     * * 右円のみの領域
     * * 2円の重なる領域
     * * 2円外の領域
     */
    getActiveArea(x, y) {
      const withinleftCircle = spotWidthin(x, y, this.leftCircle)
      const withinrightCircle = spotWidthin(x, y, this.rightCircle)

      let activeArea

      if (withinleftCircle && !withinrightCircle) {
        activeArea = AREA_SELECT_LEFT
      } else if (!withinleftCircle && withinrightCircle) {
        activeArea = AREA_SELECT_RIGHT
      } else if (withinleftCircle && withinrightCircle) {
        activeArea = AREA_SELECT_INTERSECTION
      } else {
        // 選択ファイル以外の領域は閉塞します。
        // activeArea = AREA_SELECT_OTHERS
      }

      return activeArea
    },
    /**
     * 選択範囲の顧客コード一覧ファイルをダウンロードする
     *
     * 入力ファイルに記載される顧客コードは、カード統合等で常に最新ではなくなる
     * 危険性がある。必ず逐次サーバー側で最新のコードに変換するため、2ファイルだけ
     * で完結する選択の仕方だとしてもAPIを叩く。
     */
    async download() {
      if (!this.leftFile || !this.rightFile) {
        return
      }

      this.downloading = true
      this.invalidFile = false
      this.noCustomersInSelectedAreas = false

      this.completedSteps.uploading = false
      this.completedSteps.calculating = false
      this.completedSteps.creating = false
      this.completedSteps.downloadReady = false
      this.activeStep = 'uploading'

      // Upload files
      let fileUploadPromises = [
        this.$customerGroup.upload(this.leftFile),
        this.$customerGroup.upload(this.rightFile),
      ]

      const results = await Promise.all(fileUploadPromises)
      if (results.find(r => r.invalidFile)) {
        this.invalidFile = true
        return
      }

      const customerGroupIds = results.map(r => r.customerGroupId)

      this.completedSteps.uploading = true
      this.activeStep = 'calculating'

      // Create a new customer group
      const newCustomerGroup = await this.$customerGroup.createFromCustomerGroupIds(
        ...customerGroupIds,
        [...this.selectedAreas],
      )

      if (newCustomerGroup.numRows === 0) {
        this.noCustomersInSelectedAreas = true
        return
      }

      const newCustomerGroupId = newCustomerGroup.customerGroupId

      this.completedSteps.calculating = true
      this.activeStep = 'creating'

      // archive
      this.downloadLink = await this.$customerGroup.archive(newCustomerGroupId)

      this.completedSteps.creating = true
      this.completedSteps.downloadReady = true
      this.activeStep = 'download-ready'
    },
    /**
     * 作成された顧客ファイルをダウンロード
     */
    downloadFile() {
      window.open(this.downloadLink)
    },
  },
  mounted() {
    // DnDでのファイル追加をサポートする
    document.addEventListener('dragenter', e => {
      this.dragging = true
      e.preventDefault()
    })
    document.addEventListener('dragover', e => {
      this.dragOverEvents.next(e)
      e.preventDefault()
    })
    document.addEventListener('drop', e => {
      e.preventDefault()
    })
    // dragoverイベントはドラッグ中（止まっていても）継続的に発生する。
    // 一定時間発生しなくなったらウィンドウ外に出たとみなしてドラッグ終了とする。
    this.dragOverEvents.pipe(debounceTime(200)).subscribe(e => this.endDrag(e))

    this.baseCtx = this.$refs.baseLayer.getContext('2d')
    this.baseCtx.canvas.width = this.canvas.width
    this.baseCtx.canvas.height = this.canvas.height

    this.intersectionCtx = this.$refs.intersectionLayer.getContext('2d')
    this.intersectionCtx.canvas.width = this.canvas.width
    this.intersectionCtx.canvas.height = this.canvas.height

    const layer = this.$refs.layer
    this.ctx = layer.getContext('2d')
    this.ctx.canvas.width = this.canvas.width
    this.ctx.canvas.height = this.canvas.height
    this.ctx.globalCompositeOperation = 'xor'

    layer.addEventListener('click', e => {
      const area = this.getActiveArea(e.offsetX, e.offsetY)
      if (!area) {
        return
      }

      switch (area) {
        case AREA_SELECT_LEFT:
          this.leftCircle.active = !this.leftCircle.active
          break
        case AREA_SELECT_RIGHT:
          this.rightCircle.active = !this.rightCircle.active
          break
        case AREA_SELECT_INTERSECTION:
          this.intersectionCircle.active = !this.intersectionCircle.active
          break
      }

      const copy = new Set(this.selectedAreas)
      if (this.selectedAreas.has(area)) {
        copy.delete(area)
      } else {
        copy.add(area)
      }
      this.selectedAreas = copy
      this.render()
    })

    this.render()
  },
}
</script>
<style lang="scss" scoped>
.container {
  align-items: center;
  display: flex;
  flex-direction: column;
  width: 100%;
}

.files {
  display: flex;
}
.file {
  flex: 1;
  text-align: center;
  margin-bottom: 24px;
  margin-top: 24px;
  margin-left: 16px;
  &:first-child {
    margin-left: 0;
  }
}
.file-name {
  font-size: 1rem;

  .icon {
    color: #00b0ff;
    margin-right: var(--spacing-small);
  }
}
.file-stats {
  margin-top: 8px;
}

.canvas-wrapper {
  border: 5px solid #00b0ff;
  border-radius: 5px;
  box-sizing: content-box;
  position: relative;
}

canvas,
.drop-area {
  position: absolute;
  left: 0;
  top: 0;
}

canvas {
  cursor: pointer;
}

.drop-area {
  align-items: center;
  background-color: #00b0ff;
  color: #fff;
  display: flex;
  font-size: 24px;
  justify-content: center;
}

.dnd-area-icon {
  font-size: 64px;
}

.actions {
  margin-bottom: 24px;
  margin-top: 24px;
}

.additional-info {
  margin-top: 24px;
  text-align: right;
}

.warn {
  color: var(--color-warn);
}

// Animation
.v-enter-active,
.v-leave-active {
  transition: opacity 0.1s;
}
.v-enter,
.v-leave-to {
  opacity: 0;
}
</style>
